import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/disclaimer")({
  head: () => ({
    meta: [
      { title: "Disclaimer | Acadly" },
      {
        name: "description",
        content:
          "Understand what the writing on Acadly is, what it is not, and why nothing on the Site should be treated as professional advice.",
      },
      { property: "og:title", content: "Disclaimer | Acadly" },
      {
        property: "og:description",
        content:
          "The scope, limits and correct use of the educational content published on Acadly.",
      },
      { property: "og:url", content: "https://acadly.in/disclaimer" },
    ],
    links: [{ rel: "canonical", href: "https://acadly.in/disclaimer" }],
  }),
  component: DisclaimerPage,
});

function DisclaimerPage() {
  return (
    <div className="mx-auto max-w-3xl px-5 py-16 md:py-20 prose-post">
      <div className="stamp mb-6">Legal · Disclaimer</div>
      <h1
        className="text-4xl md:text-5xl font-semibold"
        style={{ fontFamily: "var(--font-display)" }}
      >
        Disclaimer
      </h1>
      <p className="mono-label !mt-2">Last updated: 25 July 2026</p>

      <p>
        This disclaimer applies to the entire{" "}
        <a href="https://acadly.in">acadly.in</a> website (the &quot;Site&quot;)
        and to any content published on it. Please read it carefully. By using
        the Site you acknowledge and accept the points set out below. This
        disclaimer should be read together with the{" "}
        <a href="/terms">Terms and Conditions</a> and the{" "}
        <a href="/privacy-policy">Privacy Policy</a>.
      </p>

      <h2>General information only</h2>
      <p>
        Acadly publishes study notes, subject explainers and study-strategy
        articles for a general readership of Indian college students. The
        content is educational in nature and reflects the author&apos;s
        understanding at the time of writing. It is not a substitute for
        professional academic, career, legal, financial, tax, medical or
        psychological advice. If you need advice tailored to your personal
        circumstances, please consult a qualified professional.
      </p>

      <h2>Accuracy and currency</h2>
      <p>
        Reasonable care is taken to ensure that every article is accurate,
        clearly written and useful at the time of publication. Even so,
        university syllabi, examination patterns, grading rules, attendance
        norms and admission policies change from year to year, sometimes with
        little advance notice. The date shown on an article is the date on
        which its claims were correct to the author&apos;s knowledge, not a
        guarantee that they remain correct today. Before you rely on any
        time-sensitive information published on the Site, please verify it
        against a current official source such as your university bulletin,
        the University Grants Commission or the relevant examination
        authority.
      </p>

      <h2>Opinion and interpretation</h2>
      <p>
        Some articles argue in favour of a particular way of studying, a
        particular reading of a subject, or a particular career direction.
        Those pieces are opinions offered in good faith and are grounded in
        the author&apos;s reading and experience. Reasonable and equally
        well-informed readers may disagree with them. You should treat those
        articles as a starting point for your own thinking rather than as a
        final answer.
      </p>

      <h2>External links</h2>
      <p>
        Articles on the Site link to third-party websites, papers and
        resources for reference. Those websites are operated independently
        and are not under our control. Acadly is not responsible for the
        accuracy, availability, security or content of any linked website,
        and the presence of a link is not an endorsement. Please review the
        terms and privacy notice of any external website before providing
        information to it.
      </p>

      <h2>Advertising disclosure</h2>
      <p>
        The Site does not currently carry advertising. If advertising is
        introduced in future, this section will be updated with a clear
        disclosure of how it works, how advertising cookies operate, and how
        to opt out of personalised advertising. Full details will be set out
        in the <a href="/privacy-policy">Privacy Policy</a> before any ads go
        live.
      </p>

      <h2>Affiliate and sponsored content</h2>
      <p>
        Acadly does not currently participate in affiliate marketing
        programmes and does not publish sponsored articles. If either changes
        in future, the affected article will carry a clear disclosure at the
        top of the piece, in prominent text.
      </p>

      <h2>Testimonials and results</h2>
      <p>
        Reading Acadly by itself will not earn you a first-class result, secure
        admission to a master&apos;s programme or guarantee success in any
        competitive examination. Any outcome mentioned or implied in an
        article depends on your effort, preparation and factors that are
        outside the author&apos;s or the Site&apos;s control. Individual
        results will vary.
      </p>

      <h2>Fair use of images</h2>
      <p>
        Some articles include images, charts or diagrams to illustrate
        concepts. Where third-party material is used, it is used with
        permission or under a fair-dealing or fair-use rationale for
        educational commentary. If you are a rights holder and believe your
        material has been used incorrectly on the Site, please write to{" "}
        <a href="mailto:hello@acadly.in">hello@acadly.in</a> and it will be
        reviewed promptly.
      </p>

      <h2>Corrections</h2>
      <p>
        If you spot a factual error, an outdated rule or a misleading
        statement in any article, please write to{" "}
        <a href="mailto:hello@acadly.in">hello@acadly.in</a>. Corrections are
        welcomed and will be published on the affected article with a dated
        note.
      </p>

      <h2>Consent</h2>
      <p>
        By continuing to use the Site you consent to this disclaimer and to
        its most recent version, and you agree that Acadly and its author are
        not liable for any loss, damage or inconvenience arising from your
        use of the content published here.
      </p>

      <h2>Changes to this disclaimer</h2>
      <p>
        This disclaimer may be updated to reflect changes in the Site or in
        the law. When it is updated, the &quot;Last updated&quot; date at the
        top of this page will be revised. Significant changes will be
        highlighted for a reasonable period.
      </p>
    </div>
  );
}